import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Play, Pause, Square, Volume2, Music, Clock, Maximize, GripVertical, ListOrdered, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import FullscreenButton from '@/components/FullscreenButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Announcement file URLs - using backend proxy to avoid CORS issues
// NOTE: These files need to be uploaded to Cloudflare R2 with public access
// Temporary: Using silent audio as placeholder until real files are configured
const SILENT_AUDIO = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7////////////////////////////////////////////////////////////////////////AAAAAExhdmM1OC4xMzQAAAAAAAAAAAAAAAAkAAAAAAAAA4S/o9HIAAAAAAAAAAAAAAAAAAAA//sQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sQZEYP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==';

const getAnnouncementUrl = (filename) => `${BACKEND_URL}/api/audio/announcement/${filename}`;

const ANNOUNCEMENTS = {
  apparatus: {
    120: getAnnouncementUrl('twominutes_apparatus.mp3'),
    60: getAnnouncementUrl('oneminute_apparatus.mp3'),
    0: getAnnouncementUrl('warmupdone.mp3')
  },
  general: {
    120: getAnnouncementUrl('twominute_general.mp3'),
    60: getAnnouncementUrl('oneminute_general.mp3'),
    0: getAnnouncementUrl('generalwarmupdone.mp3')
  }
};

export default function MusicControlPage() {
  const [pinEntered, setPinEntered] = useState(false);
  const [pin, setPin] = useState('');
  
  // Competition & Selection
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [programmes, setProgrammes] = useState([]);
  const [selectedRound, setSelectedRound] = useState('');
  const [selectedRotation, setSelectedRotation] = useState('');
  const [rotationSchedule, setRotationSchedule] = useState(null);
  const [floorGroups, setFloorGroups] = useState([]);
  const [gymnasts, setGymnasts] = useState([]);
  const [rotationStarted, setRotationStarted] = useState(false);
  const [loadingGymnasts, setLoadingGymnasts] = useState(false);
  
  // Timer preview
  const [timerPreview, setTimerPreview] = useState(null);
  const [previewTime, setPreviewTime] = useState(0);
  const [nowPlayingPreview, setNowPlayingPreview] = useState(null);
  
  // Queue Management
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [draggingIndex, setDraggingIndex] = useState(null);
  
  // Spotify
  const [spotifyAccessToken, setSpotifyAccessToken] = useState('');
  const [spotifyRefreshToken, setSpotifyRefreshToken] = useState('');
  const [spotifyUser, setSpotifyUser] = useState(null);
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [spotifyTracks, setSpotifyTracks] = useState([]);
  const [spotifyVolume, setSpotifyVolume] = useState(50);
  const [spotifyPlayer, setSpotifyPlayer] = useState(null);
  const [spotifyDeviceId, setSpotifyDeviceId] = useState(null);
  const [spotifyReady, setSpotifyReady] = useState(false);
  const [spotifyIsPlaying, setSpotifyIsPlaying] = useState(false);
  const [spotifyCurrentTrack, setSpotifyCurrentTrack] = useState(null);
  const [spotifyConnecting, setSpotifyConnecting] = useState(false);
  
  // Floor Music
  const [floorVolume, setFloorVolume] = useState(80);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Timer
  const [timerMode, setTimerMode] = useState('apparatus');
  const [timerDuration, setTimerDuration] = useState(120);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  
  // Audio
  const floorAudioRef = useRef(null);
  const announcementAudioRef = useRef(null);
  const announcementQueue = useRef([]);
  const [isAnnouncementPlaying, setIsAnnouncementPlaying] = useState(false);
  const announcementsPlayed = useRef(new Set());
  const hasLoadedInitially = useRef(false);

  // Initialize localStorage and check for Spotify OAuth callback
  useEffect(() => {
    // Check if this is a Spotify OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      // Exchange code for tokens
      handleSpotifyCallback(code);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    const saved = localStorage.getItem('musicControlSettings');
    if (saved) {
      try {
        const settings = JSON.parse(saved);
        setSpotifyAccessToken(settings.spotifyAccessToken || '');
        setSpotifyRefreshToken(settings.spotifyRefreshToken || '');
        setSpotifyVolume(settings.spotifyVolume || 50);
        setFloorVolume(settings.floorVolume || 80);
        setSelectedCompetition(settings.selectedCompetition || '');
        setSelectedRound(settings.selectedRound || '');
        setSelectedRotation(settings.selectedRotation || '');
        
        // If we have tokens, load profile
        if (settings.spotifyAccessToken) {
          loadSpotifyProfile(settings.spotifyAccessToken);
        }
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  }, []);

  // Handle Spotify OAuth callback
  const handleSpotifyCallback = async (code) => {
    try {
      setSpotifyConnecting(true);
      const response = await axios.get(`${API}/spotify/callback?code=${code}`);
      const { access_token, refresh_token, expires_in } = response.data;
      
      setSpotifyAccessToken(access_token);
      setSpotifyRefreshToken(refresh_token);
      
      // Save tokens
      localStorage.setItem('musicControlSettings', JSON.stringify({
        ...JSON.parse(localStorage.getItem('musicControlSettings') || '{}'),
        spotifyAccessToken: access_token,
        spotifyRefreshToken: refresh_token
      }));
      
      toast.success('Spotify connected successfully!');
      loadSpotifyProfile(access_token);
    } catch (error) {
      console.error('Spotify callback error:', error);
      toast.error('Failed to connect Spotify');
    } finally {
      setSpotifyConnecting(false);
    }
  };

  // Load Spotify profile
  const loadSpotifyProfile = async (token) => {
    try {
      const response = await axios.get(`${API}/spotify/profile?access_token=${token}`);
      setSpotifyUser(response.data);
      
      if (!response.data.is_premium) {
        toast.warning('Spotify Premium is required for playback');
      }
      
      // Load playlists
      loadSpotifyPlaylists(token);
    } catch (error) {
      console.error('Spotify profile error:', error);
      // Token might be expired, clear it
      setSpotifyAccessToken('');
      setSpotifyUser(null);
    }
  };

  // Load Spotify playlists
  const loadSpotifyPlaylists = async (token) => {
    try {
      const response = await axios.get(`${API}/spotify/playlists?access_token=${token}`);
      setSpotifyPlaylists(response.data.playlists || []);
    } catch (error) {
      console.error('Spotify playlists error:', error);
    }
  };

  // Load tracks from selected playlist
  const loadPlaylistTracks = async (playlistId) => {
    if (!spotifyAccessToken) return;
    
    try {
      const response = await axios.get(
        `${API}/spotify/playlist/${playlistId}/tracks?access_token=${spotifyAccessToken}`
      );
      setSpotifyTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Spotify tracks error:', error);
      toast.error('Failed to load playlist tracks');
    }
  };

  // Connect to Spotify
  const connectSpotify = async () => {
    try {
      const response = await axios.get(`${API}/spotify/auth-url`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Spotify auth URL error:', error);
      toast.error('Failed to get Spotify login URL');
    }
  };

  // Disconnect Spotify
  const disconnectSpotify = () => {
    setSpotifyAccessToken('');
    setSpotifyRefreshToken('');
    setSpotifyUser(null);
    setSpotifyPlaylists([]);
    setSelectedPlaylist(null);
    setSpotifyTracks([]);
    
    if (spotifyPlayer) {
      spotifyPlayer.disconnect();
      setSpotifyPlayer(null);
    }
    
    // Clear from localStorage
    const settings = JSON.parse(localStorage.getItem('musicControlSettings') || '{}');
    delete settings.spotifyAccessToken;
    delete settings.spotifyRefreshToken;
    localStorage.setItem('musicControlSettings', JSON.stringify(settings));
    
    toast.success('Disconnected from Spotify');
  };

  // Save to localStorage
  const saveSettings = () => {
    const settings = {
      spotifyAccessToken,
      spotifyRefreshToken,
      spotifyVolume,
      floorVolume,
      selectedCompetition,
      selectedRound,
      selectedRotation
    };
    localStorage.setItem('musicControlSettings', JSON.stringify(settings));
  };

  useEffect(() => {
    saveSettings();
  }, [spotifyAccessToken, spotifyRefreshToken, spotifyVolume, floorVolume, selectedCompetition, selectedRound, selectedRotation]);

  // Cleanup now playing on unmount or when leaving page
  useEffect(() => {
    return () => {
      // Clear now playing when component unmounts
      if (selectedCompetition) {
        axios.post(`${API}/now-playing/stop`, { competitionId: selectedCompetition })
          .catch(err => console.error('Cleanup error:', err));
      }
    };
  }, [selectedCompetition]);

  // PIN submission
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pin === '2507') {
      setPinEntered(true);
      toast.success('Music Control Desk Active');
      loadCompetitions();
    } else {
      toast.error('Invalid PIN');
      setPin('');
    }
  };

  // Load competitions
  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/music/competitions`);
      setCompetitions(response.data.competitions || []);
    } catch (error) {
      console.error('Error loading competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  // Load programme when competition selected
  useEffect(() => {
    if (selectedCompetition) {
      loadProgramme();
    }
  }, [selectedCompetition]);

  const loadProgramme = async () => {
    try {
      const response = await axios.get(`${API}/music/programme/${selectedCompetition}`);
      const prog = response.data.programme;
      if (prog) {
        setProgrammes(prog.rounds || []);
      }
    } catch (error) {
      console.error('Error loading programme:', error);
      toast.error('Failed to load programme');
    }
  };

  // This effect is now handled by the auto-load in floorGroups effect
  // Keeping this for cleanup - remove old selectedGroups reference
  useEffect(() => {
    // Removed: loadGymnasts was triggered by selectedGroups
    // Now handled by loadGymnastsForFloor when floorGroups change
  }, []);

  // Poll timer preview and now playing every 500ms
  useEffect(() => {
    if (!selectedCompetition || !pinEntered) return;

    const pollTimerPreview = async () => {
      try {
        // Fetch both timer and now playing in parallel
        const [timerResponse, nowPlayingResponse] = await Promise.all([
          axios.get(`${API}/timer/state/${selectedCompetition}`),
          axios.get(`${API}/now-playing/${selectedCompetition}`)
        ]);
        
        const timerData = timerResponse.data.timer;
        const nowPlayingData = nowPlayingResponse.data.nowPlaying;
        
        // Update now playing preview
        setNowPlayingPreview(nowPlayingData);
        
        if (timerData && timerData.isRunning) {
          setTimerPreview(timerData);
          
          let remaining = 0;
          if (!timerData.isPaused) {
            const startTime = new Date(timerData.startedAt).getTime();
            const now = new Date().getTime();
            const elapsed = Math.floor((now - startTime) / 1000);
            remaining = Math.max(0, timerData.duration - elapsed);
            setPreviewTime(remaining);
          } else {
            remaining = timerData.remainingTime;
            setPreviewTime(remaining);
          }

          // Trigger announcements ONLY when timer is actively running (not paused) and after initial load
          if (!timerData.isPaused && hasLoadedInitially.current) {
            // Check for announcement triggers at 2:00 (120s), 1:00 (60s), and 0:00 (0s)
            const announcementTimes = [
              { seconds: 120, label: '2:00' },
              { seconds: 60, label: '1:00' },
              { seconds: 0, label: '0:00' }
            ];
            
            announcementTimes.forEach(({ seconds, label }) => {
              const key = `${timerData.id}-${seconds}`;
              
              // Check if we haven't played this announcement yet
              if (!announcementsPlayed.current.has(key)) {
                let shouldTrigger = false;
                
                if (seconds === 0) {
                  // For 0:00, trigger exactly when timer reaches 0
                  shouldTrigger = remaining === 0;
                } else {
                  // For 2:00 and 1:00, trigger when remaining time is at or just below the threshold
                  // Using 2-second window to account for polling interval
                  shouldTrigger = remaining <= seconds && remaining > (seconds - 2);
                }
                
                if (shouldTrigger) {
                  console.log(`🔔 ANNOUNCEMENT TRIGGER: ${timerData.mode} timer at ${label} (${remaining}s remaining)`);
                  announcementsPlayed.current.add(key);
                  queueAnnouncement(seconds, timerData.mode);
                }
              }
            });
          }
          
          // Mark as loaded after first poll
          if (!hasLoadedInitially.current) {
            hasLoadedInitially.current = true;
          }
        } else {
          setTimerPreview(null);
          setPreviewTime(0);
          // Clear played announcements when timer stops
          announcementsPlayed.current.clear();
        }
      } catch (error) {
        console.error('Error polling timer preview:', error);
      }
    };

    pollTimerPreview();
    const interval = setInterval(pollTimerPreview, 500);

    return () => clearInterval(interval);
  }, [selectedCompetition, pinEntered]);

  // Load rotation schedule when round changes
  useEffect(() => {
    const loadRotationSchedule = async () => {
      if (!selectedCompetition || !selectedRound) {
        setRotationSchedule(null);
        setFloorGroups([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/rotation-schedule/${selectedCompetition}/${selectedRound}`
        );
        setRotationSchedule(response.data);
      } catch (error) {
        console.error('Error loading rotation schedule:', error);
        setRotationSchedule(null);
      }
    };

    loadRotationSchedule();
  }, [selectedCompetition, selectedRound]);

  // Find groups on floor when rotation is selected
  useEffect(() => {
    if (!rotationSchedule || !selectedRotation) {
      setFloorGroups([]);
      return;
    }

    const rotNum = parseInt(selectedRotation);
    const groupsOnFloor = [];

    // Find all groups that have "floor" apparatus in the selected rotation
    (rotationSchedule.groups || []).forEach(group => {
      const rotation = (group.rotations || []).find(r => r.rotationNumber === rotNum);
      if (rotation && ['floor', 'wagfloor', 'magfloor'].includes(rotation.apparatus)) {
        groupsOnFloor.push(group.groupName);
      }
    });

    setFloorGroups(groupsOnFloor);

    if (groupsOnFloor.length === 0) {
      toast.info(`No groups on Floor for Rotation ${rotNum}`);
    } else {
      toast.success(`Groups on Floor: ${groupsOnFloor.join(', ')}`);
    }
  }, [rotationSchedule, selectedRotation]);

  // Auto-load gymnasts when floor groups are determined
  useEffect(() => {
    if (floorGroups.length > 0 && selectedCompetition && selectedRound && !rotationStarted) {
      loadGymnastsForFloor();
    }
  }, [floorGroups]);

  const loadGymnastsForFloor = async () => {
    if (floorGroups.length === 0) return;
    
    try {
      setLoadingGymnasts(true);
      const response = await axios.get(
        `${API}/music/gymnasts/${selectedCompetition}?round_number=${selectedRound}&group_names=${floorGroups.join(',')}`
      );
      const loadedGymnasts = response.data.gymnasts || [];
      setGymnasts(loadedGymnasts);
      setRotationStarted(true);
      
      if (loadedGymnasts.length === 0) {
        toast.warning('No gymnasts with floor music found');
      } else {
        toast.success(`Loaded ${loadedGymnasts.length} gymnast(s) with floor music`);
        
        // Load existing queue or initialize
        try {
          const queueResponse = await axios.get(`${API}/music-queue/${selectedCompetition}`);
          if (queueResponse.data.queue?.length > 0) {
            setQueue(queueResponse.data.queue);
            setQueueIndex(queueResponse.data.currentIndex || 0);
          }
        } catch (err) {
          console.error('Error loading queue:', err);
        }
      }
    } catch (error) {
      console.error('Error loading gymnasts:', error);
      toast.error('Failed to load gymnasts');
    } finally {
      setLoadingGymnasts(false);
    }
  };

  const loadGymnasts = async () => {
    try {
      setLoadingGymnasts(true);
      const response = await axios.get(
        `${API}/music/gymnasts/${selectedCompetition}?round_number=${selectedRound}&group_names=${floorGroups.join(',')}`
      );
      const loadedGymnasts = response.data.gymnasts || [];
      setGymnasts(loadedGymnasts);
      
      if (loadedGymnasts.length === 0) {
        toast.warning('No gymnasts with floor music found in selected groups');
      } else {
        toast.success(`Loaded ${loadedGymnasts.length} gymnast(s) with floor music`);
        
        // Load existing queue or initialize with loaded gymnasts
        try {
          const queueResponse = await axios.get(`${API}/music-queue/${selectedCompetition}`);
          if (queueResponse.data.queue?.length > 0) {
            setQueue(queueResponse.data.queue);
            setQueueIndex(queueResponse.data.currentIndex || 0);
          } else {
            // Initialize queue with loaded gymnasts in their default order
            const initialQueue = loadedGymnasts.map(g => ({
              id: g.id,
              number: g.number,
              letter: g.letter || '',
              firstName: g.preferredFirstName,
              lastName: g.preferredLastName,
              clubName: g.clubName || '',
              floorMusicUrl: g.floorMusicUrl
            }));
            setQueue(initialQueue);
            setQueueIndex(0);
            // Save initial queue
            await axios.put(`${API}/music-queue/${selectedCompetition}`, {
              gymnasts: initialQueue,
              currentIndex: 0
            });
          }
        } catch (err) {
          console.error('Error loading queue:', err);
        }
      }
    } catch (error) {
      console.error('Error loading gymnasts:', error);
      toast.error('Failed to load gymnasts with music');
    } finally {
      setLoadingGymnasts(false);
    }
  };

  // Save queue to backend
  const saveQueue = async (newQueue, newIndex = queueIndex) => {
    try {
      await axios.put(`${API}/music-queue/${selectedCompetition}`, {
        gymnasts: newQueue,
        currentIndex: newIndex
      });
    } catch (error) {
      console.error('Error saving queue:', error);
    }
  };

  // Move gymnast in queue (drag and drop)
  const moveInQueue = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const newQueue = [...queue];
    const [moved] = newQueue.splice(fromIndex, 1);
    newQueue.splice(toIndex, 0, moved);
    
    setQueue(newQueue);
    
    // Adjust current index if needed
    let newIndex = queueIndex;
    if (fromIndex === queueIndex) {
      newIndex = toIndex;
    } else if (fromIndex < queueIndex && toIndex >= queueIndex) {
      newIndex = queueIndex - 1;
    } else if (fromIndex > queueIndex && toIndex <= queueIndex) {
      newIndex = queueIndex + 1;
    }
    
    setQueueIndex(newIndex);
    saveQueue(newQueue, newIndex);
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    if (draggingIndex !== null && draggingIndex !== toIndex) {
      moveInQueue(draggingIndex, toIndex);
    }
    setDraggingIndex(null);
  };

  // Advance to next in queue
  const advanceQueue = async () => {
    const nextIndex = queueIndex + 1;
    if (nextIndex < queue.length) {
      setQueueIndex(nextIndex);
      await axios.post(`${API}/music-queue/${selectedCompetition}/set-index`, { index: nextIndex });
    }
  };

  // Add gymnast to queue
  const addToQueue = (gymnast) => {
    // Check if already in queue
    if (queue.some(q => q.id === gymnast.id)) {
      toast.error('Already in order');
      return;
    }
    
    const newGymnast = {
      id: gymnast.id,
      number: gymnast.number,
      letter: gymnast.letter || '',
      firstName: gymnast.preferredFirstName || gymnast.firstName,
      lastName: gymnast.preferredLastName || gymnast.lastName,
      clubName: gymnast.clubName || '',
      floorMusicUrl: gymnast.floorMusicUrl
    };
    
    const newQueue = [...queue, newGymnast];
    setQueue(newQueue);
    saveQueue(newQueue);
    toast.success(`Added ${newGymnast.firstName} to order`);
  };

  // Remove gymnast from queue
  const removeFromQueue = (index) => {
    // Don't allow removing if currently playing
    if (index === queueIndex && isPlaying) {
      toast.error("Can't remove while playing");
      return;
    }
    
    const newQueue = queue.filter((_, i) => i !== index);
    
    // Adjust current index if needed
    let newIndex = queueIndex;
    if (index < queueIndex) {
      newIndex = queueIndex - 1;
    } else if (index === queueIndex && queueIndex >= newQueue.length) {
      newIndex = Math.max(0, newQueue.length - 1);
    }
    
    setQueue(newQueue);
    setQueueIndex(newIndex);
    saveQueue(newQueue, newIndex);
  };

  // Check if gymnast is in queue
  const isInQueue = (gymnastId) => {
    return queue.some(q => q.id === gymnastId);
  };

  // Spotify Web Playback SDK initialization
  useEffect(() => {
    if (!spotifyAccessToken || !pinEntered) return;

    // Load Spotify SDK
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    script.onerror = () => {
      console.error('Failed to load Spotify SDK');
      toast.error('Spotify SDK failed to load');
    };
    document.body.appendChild(script);

    window.onSpotifyWebPlaybackSDKReady = () => {
      const player = new window.Spotify.Player({
        name: 'Scorz Music Control',
        getOAuthToken: cb => { cb(spotifyAccessToken); },
        volume: spotifyVolume / 100
      });

      // Error handling
      player.addListener('initialization_error', ({ message }) => {
        console.error('Spotify init error:', message);
      });
      player.addListener('authentication_error', ({ message }) => {
        console.error('Spotify auth error:', message);
        toast.error('Spotify authentication failed. Please reconnect.');
        disconnectSpotify();
      });
      player.addListener('account_error', ({ message }) => {
        console.error('Spotify account error:', message);
        toast.error('Spotify Premium is required for playback');
      });
      player.addListener('playback_error', ({ message }) => {
        console.error('Spotify playback error:', message);
      });

      // Playback status updates
      player.addListener('player_state_changed', state => {
        if (!state) return;
        setSpotifyIsPlaying(!state.paused);
        if (state.track_window?.current_track) {
          setSpotifyCurrentTrack({
            name: state.track_window.current_track.name,
            artists: state.track_window.current_track.artists.map(a => a.name).join(', '),
            album_art: state.track_window.current_track.album.images[0]?.url
          });
        }
      });

      // Ready
      player.addListener('ready', ({ device_id }) => {
        console.log('Spotify ready with device ID:', device_id);
        setSpotifyDeviceId(device_id);
        setSpotifyReady(true);
        toast.success('Spotify connected and ready!');
      });

      // Not Ready
      player.addListener('not_ready', ({ device_id }) => {
        console.log('Spotify device offline:', device_id);
        setSpotifyReady(false);
      });

      // Connect to the player
      player.connect().then(success => {
        if (success) {
          console.log('Spotify player connected');
        }
      });

      setSpotifyPlayer(player);
    };

    return () => {
      if (spotifyPlayer) {
        spotifyPlayer.disconnect();
      }
    };
  }, [spotifyAccessToken, pinEntered]);

  // Spotify playback controls
  const playSpotifyTrack = async (trackUri) => {
    if (!spotifyAccessToken || !spotifyDeviceId) {
      toast.error('Spotify not ready');
      return;
    }
    
    try {
      await axios.post(`${API}/spotify/play`, {
        access_token: spotifyAccessToken,
        track_uri: trackUri,
        device_id: spotifyDeviceId
      });
      setSpotifyIsPlaying(true);
    } catch (error) {
      console.error('Spotify play error:', error);
      toast.error('Failed to play track');
    }
  };

  const pauseSpotify = async () => {
    if (!spotifyAccessToken) return;
    
    try {
      await axios.post(`${API}/spotify/pause`, {
        access_token: spotifyAccessToken,
        device_id: spotifyDeviceId
      });
      setSpotifyIsPlaying(false);
    } catch (error) {
      console.error('Spotify pause error:', error);
    }
  };

  const setSpotifyVolumeLevel = async (volume) => {
    setSpotifyVolume(volume);
    if (spotifyPlayer) {
      spotifyPlayer.setVolume(volume / 100);
    }
  };

  // Helper to get the proxied audio URL for a gymnast
  const getFloorMusicUrl = (gymnastId) => {
    return `${API}/audio/floor-music/${gymnastId}`;
  };

  // Floor music playback
  const playFloorMusic = (url, gymnastData) => {
    if (!floorAudioRef.current) {
      toast.error('Audio player not ready');
      return;
    }

    if (!url) {
      toast.error('No floor music available for this gymnast');
      return;
    }

    const gymnastName = `${gymnastData.preferredFirstName} ${gymnastData.preferredLastName}`;
    // Use the proxy endpoint to avoid CORS issues
    const proxyUrl = getFloorMusicUrl(gymnastData.id);

    // Duck Spotify volume
    duckSpotifyVolume();

    floorAudioRef.current.src = proxyUrl;
    floorAudioRef.current.volume = floorVolume / 100;
    
    floorAudioRef.current.play()
      .then(async () => {
        setCurrentTrack(gymnastName);
        setIsPlaying(true);
        toast.success(`Playing: ${gymnastName}`);
        
        // Send now playing info to backend
        try {
          await axios.post(`${API}/now-playing/start`, {
            competitionId: selectedCompetition,
            gymnast: {
              id: gymnastData.id,
              number: gymnastData.number,
              letter: gymnastData.letter || "",
              name: gymnastName,
              clubName: gymnastData.clubName || ""
            }
          });
        } catch (error) {
          console.error('Error updating now playing:', error);
        }
      })
      .catch(error => {
        console.error('Error playing audio:', error);
        toast.error(`Failed to play music: ${error.message}`);
        restoreSpotifyVolume();
      });
  };

  // Floor music ended
  const handleFloorMusicEnded = async () => {
    setIsPlaying(false);
    setCurrentTrack(null);
    setProgress(0);
    
    // Immediately clear the now playing preview for instant UI update
    setNowPlayingPreview(null);

    // Clear now playing in backend
    try {
      await axios.post(`${API}/now-playing/stop`, {
        competitionId: selectedCompetition
      });
      console.log('Now playing cleared successfully');
    } catch (error) {
      console.error('Error clearing now playing:', error);
    }

    // Advance queue to next gymnast
    await advanceQueue();

    // Restore Spotify volume after 2 seconds
    setTimeout(() => {
      restoreSpotifyVolume();
    }, 2000);
  };

  // Stop floor music playback
  const stopFloorMusic = async () => {
    if (floorAudioRef.current) {
      floorAudioRef.current.pause();
      floorAudioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTrack(null);
    setProgress(0);
    setNowPlayingPreview(null);
    
    // Clear now playing in backend
    try {
      await axios.post(`${API}/now-playing/stop`, {
        competitionId: selectedCompetition
      });
    } catch (error) {
      console.error('Error clearing now playing:', error);
    }
    
    restoreSpotifyVolume();
  };

  // Duck Spotify volume (smooth fade to 0%)
  const duckSpotifyVolume = () => {
    if (!spotifyPlayer) return;
    spotifyPlayer.setVolume(0);
  };

  // Restore Spotify volume (smooth fade)
  const restoreSpotifyVolume = () => {
    if (!spotifyPlayer) return;
    spotifyPlayer.setVolume(spotifyVolume / 100);
  };

  // Timer controls
  const startTimer = async () => {
    if (!selectedCompetition) {
      toast.error('Please select a competition first');
      return;
    }
    
    try {
      // Clear played announcements for new timer
      announcementsPlayed.current.clear();
      
      console.log('Starting timer:', { competitionId: selectedCompetition, mode: timerMode, duration: timerDuration });
      
      const response = await axios.post(`${API}/timer/start`, {
        competitionId: selectedCompetition,
        mode: timerMode,
        duration: timerDuration
      });
      
      console.log('Timer started successfully:', response.data);
      
      setTimerRunning(true);
      setTimerPaused(false);
      toast.success(`${timerMode === 'apparatus' ? 'Apparatus' : 'General'} timer started`);
    } catch (error) {
      console.error('Error starting timer:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to start timer: ${error.response?.data?.detail || error.message}`);
    }
  };

  const pauseTimer = async () => {
    try {
      await axios.post(`${API}/timer/pause`, {
        competitionId: selectedCompetition
      });
      setTimerPaused(true);
      toast.success('Timer paused');
    } catch (error) {
      console.error('Error pausing timer:', error);
    }
  };

  const resumeTimer = async () => {
    try {
      await axios.post(`${API}/timer/resume`, {
        competitionId: selectedCompetition
      });
      setTimerPaused(false);
      toast.success('Timer resumed');
    } catch (error) {
      console.error('Error resuming timer:', error);
    }
  };

  const stopTimer = async () => {
    try {
      await axios.post(`${API}/timer/stop`, {
        competitionId: selectedCompetition
      });
      setTimerRunning(false);
      setTimerPaused(false);
      toast.success('Timer stopped');
    } catch (error) {
      console.error('Error stopping timer:', error);
    }
  };

  const queueAnnouncement = (timeRemaining, mode) => {
    // timeRemaining is in seconds (120, 60, or 0)
    const url = ANNOUNCEMENTS[mode][timeRemaining];
    
    if (url) {
      console.log(`📢 QUEUEING ANNOUNCEMENT: ${mode} at ${timeRemaining}s`);
      console.log(`   URL: ${url}`);
      console.log(`   Queue length before: ${announcementQueue.current.length}`);
      announcementQueue.current.push(url);
      console.log(`   Queue length after: ${announcementQueue.current.length}`);
      processAnnouncementQueue();
    } else {
      console.error(`❌ NO ANNOUNCEMENT FOUND for ${mode} at ${timeRemaining}s`);
      console.error(`   Available announcements:`, ANNOUNCEMENTS);
    }
  };

  const processAnnouncementQueue = () => {
    console.log(`🎵 Processing announcement queue. Playing: ${isAnnouncementPlaying}, Queue: ${announcementQueue.current.length}`);
    
    if (isAnnouncementPlaying) {
      console.log('   ⏸️ Already playing an announcement, waiting...');
      return;
    }
    
    if (announcementQueue.current.length === 0) {
      console.log('   📭 Queue is empty');
      return;
    }

    const url = announcementQueue.current.shift();
    setIsAnnouncementPlaying(true);

    // Duck Spotify
    duckSpotifyVolume();

    console.log(`   ▶️ PLAYING ANNOUNCEMENT: ${url}`);
    announcementAudioRef.current.src = url;
    announcementAudioRef.current.play()
      .then(() => {
        console.log('   ✅ Announcement started playing successfully');
        toast.info('Playing warmup announcement', { duration: 2000 });
      })
      .catch(error => {
        console.error('   ❌ Error playing announcement:', error);
        toast.error(`Announcement failed: ${error.message}`);
        setIsAnnouncementPlaying(false);
        restoreSpotifyVolume();
        // Try next in queue
        setTimeout(() => processAnnouncementQueue(), 500);
      });
  };

  const handleAnnouncementEnded = () => {
    setIsAnnouncementPlaying(false);

    // Restore Spotify after 2 seconds
    setTimeout(() => {
      restoreSpotifyVolume();
      processAnnouncementQueue(); // Process next in queue
    }, 2000);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!pinEntered) return;

      switch (e.key) {
        case 'Escape':
          if (isPlaying && floorAudioRef.current) {
            floorAudioRef.current.pause();
            handleFloorMusicEnded(); // Properly clear now playing
          }
          break;
        case 't':
        case 'T':
          if (timerRunning) {
            if (timerPaused) {
              resumeTimer();
            } else {
              pauseTimer();
            }
          } else {
            startTimer();
          }
          break;
        case 'p':
        case 'P':
          if (isPlaying && floorAudioRef.current) {
            floorAudioRef.current.pause();
            handleFloorMusicEnded(); // Properly clear now playing
          }
          break;
        case 'r':
        case 'R':
          if (timerRunning) {
            stopTimer();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pinEntered, isPlaying, timerRunning, timerPaused]);

  if (!pinEntered) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-gray-200">
          <CardHeader className="text-center">
            <img src="/scorz-logo.png" alt="Scorz Logo" className="h-16 mx-auto mb-4" />
            <CardTitle className="text-2xl font-bold text-black">
              Music Control Desk
            </CardTitle>
            <p className="text-gray-500 text-sm mt-2">
              Enter PIN to access
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter PIN"
                className="text-center text-2xl tracking-widest"
                maxLength={4}
                autoFocus
              />
              <Button type="submit" className="w-full bg-black hover:bg-gray-800">
                Access Music Control
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 p-3 overflow-hidden">
      <div className="h-full flex flex-col gap-3">
        {/* Header Row - Competition Selection */}
        <div className="flex items-center gap-4 bg-white rounded-lg p-3 shadow-sm">
          <img src="/scorz-logo.png" alt="Scorz" className="h-8" />
          <div className="flex-1 flex items-center gap-3">
            <Select value={selectedCompetition} onValueChange={(val) => {
              setSelectedCompetition(val);
              setSelectedRound('');
              setSelectedRotation('');
              setRotationStarted(false);
              setGymnasts([]);
              setFloorGroups([]);
            }}>
              <SelectTrigger className="w-[200px]" data-testid="competition-select">
                <SelectValue placeholder="Competition" />
              </SelectTrigger>
              <SelectContent>
                {competitions.map(comp => (
                  <SelectItem key={comp.id} value={comp.id}>{comp.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedRound} onValueChange={(val) => {
              setSelectedRound(val);
              setSelectedRotation('');
              setRotationStarted(false);
              setGymnasts([]);
              setFloorGroups([]);
            }} disabled={!selectedCompetition}>
              <SelectTrigger className="w-[140px]" data-testid="round-select">
                <SelectValue placeholder="Round" />
              </SelectTrigger>
              <SelectContent>
                {programmes.map(round => (
                  <SelectItem key={round.roundNumber} value={round.roundNumber.toString()}>
                    {round.roundName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Rotation Selector */}
            <Select value={selectedRotation} onValueChange={(val) => {
              setSelectedRotation(val);
              setRotationStarted(false);
              setGymnasts([]);
            }} disabled={!selectedRound}>
              <SelectTrigger className="w-[140px]" data-testid="rotation-select">
                <SelectValue placeholder="Rotation" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map(num => (
                  <SelectItem key={num} value={num.toString()}>
                    Rotation {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Show groups on floor */}
            {floorGroups.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-100 rounded-lg">
                <span className="text-sm text-green-800 font-medium">Floor:</span>
                {floorGroups.map(group => (
                  <span key={group} className="text-sm font-semibold text-green-700 bg-green-200 px-2 py-0.5 rounded">
                    {group}
                  </span>
                ))}
              </div>
            )}
            
            {rotationStarted && (
              <Button 
                onClick={() => { 
                  setRotationStarted(false); 
                  setGymnasts([]); 
                  setSelectedRotation('');
                  setFloorGroups([]);
                }} 
                variant="destructive"
                size="sm"
                data-testid="stop-btn"
              >
                <Square className="w-4 h-4 mr-1" /> Stop
              </Button>
            )}
          </div>

          {/* Volume Control - compact */}
          <div className="flex items-center gap-2 border-l pl-3">
            <Volume2 className="w-4 h-4 text-gray-500" />
            <Slider
              value={[floorVolume]}
              onValueChange={([val]) => setFloorVolume(val)}
              max={100}
              className="w-24"
            />
            <span className="text-xs text-gray-500 w-8">{floorVolume}%</span>
          </div>
          
          {/* Fullscreen Button */}
          <FullscreenButton />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex gap-3 min-h-0">
          {/* Gymnasts Grid - Main Area */}
          <div className="flex-1 bg-white rounded-lg shadow-sm p-3 overflow-auto">
            {!rotationStarted ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Music className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p className="text-lg">Select competition, round & rotation</p>
                  <p className="text-sm mt-2">Groups on Floor will be loaded automatically from the rotation schedule</p>
                </div>
              </div>
            ) : loadingGymnasts ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-lg text-gray-500">Loading gymnasts...</div>
              </div>
            ) : gymnasts.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No gymnasts with floor music in {floorGroups.join(', ') || 'selected groups'}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-2">
                {gymnasts.map(gymnast => {
                  const isCurrentlyPlaying = currentTrack === `${gymnast.preferredFirstName} ${gymnast.preferredLastName}`;
                  const inQueue = isInQueue(gymnast.id);
                  return (
                    <div key={gymnast.id} className="relative">
                      <Button
                        onClick={() => playFloorMusic(gymnast.floorMusicUrl, gymnast)}
                        className={`h-24 w-full flex flex-col items-center justify-center p-1 transition-all ${
                          isCurrentlyPlaying 
                            ? 'bg-green-600 hover:bg-green-700 text-white ring-4 ring-green-300' 
                            : inQueue
                            ? 'bg-blue-50 hover:bg-blue-100 text-gray-900 border-2 border-blue-400'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'
                        }`}
                        variant="ghost"
                      >
                        <div className={`font-bold text-xl ${isCurrentlyPlaying ? 'text-white' : 'text-black'}`}>
                          {gymnast.number}{gymnast.letter}
                        </div>
                        <div className={`text-xs truncate w-full text-center ${isCurrentlyPlaying ? 'text-white' : 'text-gray-700'}`}>
                          {gymnast.preferredFirstName}
                        </div>
                        <div className={`text-xs truncate w-full text-center ${isCurrentlyPlaying ? 'text-green-100' : 'text-gray-500'}`}>
                          {gymnast.preferredLastName}
                        </div>
                      </Button>
                      {/* Add/Remove from queue button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (inQueue) {
                            const idx = queue.findIndex(q => q.id === gymnast.id);
                            if (idx !== -1) removeFromQueue(idx);
                          } else {
                            addToQueue(gymnast);
                          }
                        }}
                        className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs shadow-md transition-colors ${
                          inQueue 
                            ? 'bg-red-500 hover:bg-red-600' 
                            : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                        title={inQueue ? 'Remove from order' : 'Add to order'}
                      >
                        {inQueue ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Sidebar - Now Playing & Controls */}
          <div className="w-72 flex flex-col gap-3">
            {/* Now Playing */}
            <div className={`rounded-lg p-4 shadow-sm ${isPlaying ? 'bg-green-600 text-white' : 'bg-white'}`}>
              <div className="text-xs font-semibold uppercase opacity-75 mb-2">
                {isPlaying ? 'Now Playing' : 'Ready'}
              </div>
              {nowPlayingPreview ? (
                <div className="text-center">
                  <div className="text-4xl font-bold">
                    {nowPlayingPreview.gymnastNumber}{nowPlayingPreview.gymnastLetter}
                  </div>
                  <div className="text-lg font-semibold mt-1">{nowPlayingPreview.gymnastName}</div>
                  <div className="text-sm opacity-75">{nowPlayingPreview.clubName}</div>
                </div>
              ) : (
                <div className="text-center text-gray-400 py-4">
                  <Music className="w-8 h-8 mx-auto mb-1 opacity-50" />
                  <p className="text-sm">Click gymnast to play</p>
                </div>
              )}
              
              {isPlaying && (
                <div className="mt-3 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="flex-1"
                    onClick={() => floorAudioRef.current?.pause()}
                  >
                    <Pause className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="flex-1"
                    onClick={stopFloorMusic}
                  >
                    <Square className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Timer Controls - Compact */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase text-gray-500">Timer</span>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant={timerMode === 'general' ? 'default' : 'outline'}
                    className={`text-xs px-2 h-6 ${timerMode === 'general' ? 'bg-black' : ''}`}
                    onClick={() => setTimerMode('general')}
                  >
                    General
                  </Button>
                  <Button 
                    size="sm" 
                    variant={timerMode === 'apparatus' ? 'default' : 'outline'}
                    className={`text-xs px-2 h-6 ${timerMode === 'apparatus' ? 'bg-black' : ''}`}
                    onClick={() => setTimerMode('apparatus')}
                  >
                    Apparatus
                  </Button>
                </div>
              </div>
              
              <div className="text-center py-2">
                <div className="text-3xl font-bold tabular-nums">
                  {Math.floor(previewTime / 60)}:{(previewTime % 60).toString().padStart(2, '0')}
                </div>
              </div>
              
              <div className="flex gap-1">
                {!timerRunning ? (
                  <Button onClick={startTimer} size="sm" className="flex-1 bg-green-600 hover:bg-green-700">
                    <Play className="w-4 h-4" />
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={timerPaused ? resumeTimer : pauseTimer} 
                      size="sm" 
                      className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                    >
                      {timerPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </Button>
                    <Button onClick={stopTimer} size="sm" variant="destructive" className="flex-1">
                      <Square className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Timer Duration Controls */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Set Time</div>
              
              {/* Current Duration Display */}
              <div className="text-center mb-3">
                <span className="text-3xl font-bold tabular-nums">
                  {Math.floor(timerDuration / 60)}:{(timerDuration % 60).toString().padStart(2, '0')}
                </span>
              </div>
              
              {/* Increment/Decrement Buttons */}
              <div className="grid grid-cols-4 gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs font-semibold text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => setTimerDuration(prev => Math.max(0, prev - 60))}
                  disabled={timerRunning || timerDuration < 60}
                  data-testid="timer-minus-1m"
                >
                  -1m
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs font-semibold text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => setTimerDuration(prev => Math.max(0, prev - 30))}
                  disabled={timerRunning || timerDuration < 30}
                  data-testid="timer-minus-30s"
                >
                  -30s
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs font-semibold text-green-600 border-green-300 hover:bg-green-50"
                  onClick={() => setTimerDuration(prev => prev + 30)}
                  disabled={timerRunning}
                  data-testid="timer-plus-30s"
                >
                  +30s
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs font-semibold text-green-600 border-green-300 hover:bg-green-50"
                  onClick={() => setTimerDuration(prev => prev + 60)}
                  disabled={timerRunning}
                  data-testid="timer-plus-1m"
                >
                  +1m
                </Button>
              </div>
              
              {/* Reset to 0 button */}
              <Button
                size="sm"
                variant="ghost"
                className="w-full mt-2 text-xs text-gray-500"
                onClick={() => setTimerDuration(0)}
                disabled={timerRunning || timerDuration === 0}
              >
                Reset to 0:00
              </Button>
            </div>

            {/* Performance Order Queue - Takes remaining space */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden flex-1 flex flex-col min-h-[300px]">
              <div className="flex items-center justify-between p-2 border-b bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <ListOrdered className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-semibold uppercase text-gray-600">Performance Order</span>
                </div>
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">{queue.length}</span>
              </div>
              
              {queue.length > 0 ? (
                <div className="overflow-y-auto flex-1">
                  {queue.map((gymnast, index) => {
                    const isCurrent = index === queueIndex;
                    const isNext = index === queueIndex + 1;
                    const isPast = index < queueIndex;
                    
                    return (
                      <div
                        key={`${gymnast.id}-${index}`}
                        draggable={!isPlaying || index !== queueIndex}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`flex items-center gap-1 py-1 px-1.5 border-b cursor-move transition-colors ${
                          isCurrent ? 'bg-green-100 border-l-4 border-l-green-500' :
                          isNext ? 'bg-blue-50 border-l-4 border-l-blue-400' :
                          isPast ? 'bg-gray-100 opacity-50' :
                          'hover:bg-gray-50 border-l-4 border-l-transparent'
                        } ${draggingIndex === index ? 'opacity-50' : ''}`}
                      >
                        <GripVertical className="w-3 h-3 text-gray-300 flex-shrink-0" />
                        <span className="text-[10px] font-mono text-gray-400 w-3">{index + 1}</span>
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <span className="font-bold text-xs">{gymnast.number}{gymnast.letter}</span>
                          <span className="text-xs truncate">{gymnast.firstName}</span>
                        </div>
                        {isCurrent && (
                          <span className="text-[9px] bg-green-500 text-white px-1 rounded flex-shrink-0">NOW</span>
                        )}
                        {isNext && (
                          <span className="text-[9px] bg-blue-500 text-white px-1 rounded flex-shrink-0">NEXT</span>
                        )}
                        {!isCurrent && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromQueue(index);
                            }}
                            className="p-0.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 flex-shrink-0"
                            title="Remove from order"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center p-4">
                  <div className="text-center text-gray-400">
                    <ListOrdered className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">Click + on gymnasts to add to order</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Keyboard shortcuts */}
        <div className="text-xs text-gray-500 text-center py-1">
          <span className="font-semibold">Shortcuts:</span> Esc (Stop) | T (Timer) | P (Pause) | R (Reset)
        </div>
      </div>

      {/* Hidden audio elements */}
      <audio
        ref={floorAudioRef}
        onEnded={handleFloorMusicEnded}
        onTimeUpdate={(e) => {
          setProgress(e.target.currentTime);
          setDuration(e.target.duration);
        }}
        onError={(e) => {
          console.error('Floor music error:', e);
          setIsPlaying(false);
          setNowPlayingPreview(null);
          restoreSpotifyVolume();
          axios.post(`${API}/now-playing/stop`, { competitionId: selectedCompetition }).catch(err => console.error('Error clearing now playing:', err));
        }}
      />
      <audio
        ref={announcementAudioRef}
        onEnded={handleAnnouncementEnded}
        onError={(e) => {
          console.error('Announcement error:', e);
          setIsAnnouncementPlaying(false);
          restoreSpotifyVolume();
          processAnnouncementQueue();
        }}
      />
    </div>
  );
}
